import React, {useState, useEffect, useRef} from 'react';
import PropTypes from 'prop-types';
import {useSelector, useDispatch} from 'react-redux';
import {useHistory, useParams} from 'react-router-dom';
import {makeStyles} from '@material-ui/core/styles';
import _debounce from 'lodash/debounce';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CircularProgress from '@material-ui/core/CircularProgress';
import SearchIcon from '@material-ui/icons/Search';
import AgencyIcon from '@material-ui/icons/Store';
import AgentIcon from '@material-ui/icons/Person';
import SuburbIcon from '@material-ui/icons/LocationOn';
import {startFindAgentSearch, resetFindAgentSearch} from '../../actions';
import styles from './SuburbTypeahead.scss';
import {titleCase, removeDashes} from '../../lib/urlTools';
import {breakpointMdHigh} from '../../lib/config';
import {escapeSymbols} from '../../lib/formatting';

const useStyles = makeStyles({
  popper: {
    zIndex: 5002,
  },
});

const highlighter = (name, highlight) => {
  let reg = new RegExp(`(${escapeSymbols(highlight)})`, 'igm');
  let nameArr = name.split(reg);
  return nameArr.map((item, index) => {
    if (item != '') {
      if (item.toLowerCase() === highlight.toLowerCase()) return <span key={index} className={styles.highlight}>{item}</span>;
      else return <span key={index}>{item}</span>;
    }
  });
};

function SuburbTypeahead(props) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [defaultOption, setDefaultOption] = useState(null);
  const [options, setOptions] = useState([]);
  const dispatch = useDispatch();
  const data = useSelector(state => state.findAgentSearch);
  let history = useHistory();
  const params = useParams();
  const acRoot = useRef();
  const {scrollToTop} = props;
  const classes = useStyles();

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    function toggleIsMobile() {
      setIsMobile(window.innerWidth <= breakpointMdHigh ? true : false);
    }
    window.addEventListener('resize', toggleIsMobile);
    toggleIsMobile();
    return () => window.removeEventListener('resize', toggleIsMobile);
  }, []);

  if (params?.suburb && !defaultOption) {
    setDefaultOption({id: `${params.suburb.replace(/-/ig, '_')}+${params.stateCode}+${params.postcode}`.toUpperCase(), type:"Suburb", suburb: removeDashes(params.suburb).toUpperCase(), stateCode: params.stateCode.toUpperCase(), postCode: params.postcode});
  }

  useEffect(() => {
    if (!params.suburb) setOptions([]);
    return () => {
      setOptions([]);
    };
  }, []);

  useEffect(() => {
    switch (data?.status) {
      case "fetching":
        if (!loading) setLoading(true);
        break;
      case "success":
        setOptions(data.results.content);
      case "failure":
        setLoading(false);
        break;
      default:
        break;
    }
  }, [data]);

  const doSearch = (newSearchTerm) => {
    dispatch(startFindAgentSearch(encodeURIComponent(newSearchTerm)));
  };
  const searchRequest = _debounce(doSearch, 350);

  const changeHandler = (event, value, reason) => {
    switch (reason) {
      case "input":
        searchRequest(value);
        break;
      default:
        break;
    }
  }

  const optionRender = (option, {inputValue}) => {
    let element;
    switch (option.type) {
      case "Agent":
        let label = option.name;
        if (option.name.toLowerCase() !== option.agencyName.toLowerCase()) label = `${option.name} - ${option.agencyName}`;
        element = <div><AgentIcon className={styles.resultIcon} /> {highlighter(label, inputValue)}</div>
        break;
      case "Agency":
        element = <div className="d-flex flex-row align-items-center"><div className="mr-2"><AgencyIcon className={styles.resultIcon} /></div><div className="flex-grow-1">{highlighter(option.name, inputValue)}</div></div>
        break;
      case "Suburb":
        element = <div className="d-flex flex-row align-items-center"><div className="mr-2"><SuburbIcon className={styles.resultIcon} /></div><div className="flex-grow-1">{highlighter(`${titleCase(option.suburb)}, ${option.stateCode} ${option.postCode}`, inputValue)}</div></div>
        break;
      default:
        element = null;
        break;
    }
    return element;
  }

  const optionLabel = (option) => {
    let label;
    switch (option.type) {
      case "Agent":
        label = option.name;
        if (option.name.toLowerCase() !== option.agencyName.toLowerCase()) label = `${option.name} - ${option.agencyName}`;
        break;
      case "Agency":
        label = option.name;
        break;
      case "Suburb":
        label = `${titleCase(option.suburb)}, ${option.stateCode} ${option.postCode}`;
        break;
      default:
        label = '';
        break;
    }
    return label;
  }

  const optionSelected = (event, option, reason) => {
    if (reason === 'select-option') {
      switch(option.type) {
        case "Agent":
          let agentUrl = option?.links.find((item) => item.rel === 'othWebAgentUrl');
          if (agentUrl) agentUrl = agentUrl.href.replace(/https.*onthehouse.com.au/, '');
          history.push(agentUrl, {from: 'search-agent'});
          break;
        case "Agency":
          let agencyUrl = option?.links.find((item) => item.rel === 'othWebAgencyUrl');
          if (agencyUrl) agencyUrl = agencyUrl.href.replace(/https.*onthehouse.com.au/, '');
          history.push(agencyUrl, {from: 'search-agency'});
          break;
        case "Suburb":
          let suburbUrl = `/real-estate-agents/${option.stateCode}/${option.suburb.replace(/-/ig, '--').replace(/\s/ig, '-')}-${option.postCode}`;
          suburbUrl = suburbUrl.toLowerCase();
          history.push(suburbUrl);
          break;
        default:
          break;
      }
    }
  }

  const scrollHandler = (e) => {
    if (scrollToTop && acRoot?.current) acRoot.current.parentElement.parentElement.scrollIntoView();
  }

  const getNoOptionsText = () => {
    if (data.status === 'success' && data.results?.content?.length === 0) {
      return 'No results found. Please try another search.';
    }
    return 'Start typing to search';
  };

  return (
    <Autocomplete
      ref={acRoot}
      autoHighlight={true}
      id="suburb-typeahead"
      className={styles.typeahead}
      classes={{
        popper: classes.popper,
      }}
      clearOnBlur={false}
      defaultValue={defaultOption}
      getOptionLabel={optionLabel}
      getOptionSelected={(option, value) => option.id === value.id}
      options={options}
      filterOptions={(options) => options}
      loading={loading}
      noOptionsText={getNoOptionsText()}
      onChange={optionSelected}
      onInputChange={changeHandler}
      onOpen={() => {
        setOpen(true);
      }}
      onClose={() => {
        setOpen(false);
      }}
      open={open}
      renderOption={optionRender}
      renderInput={(params) => (
        <TextField
          {...params}
          className={styles.textField}
          label={null}
          variant="outlined"
          placeholder="Search by suburb, agency or agent"
          onFocus={isMobile ? scrollHandler : null}
          InputProps={{
            ...params.InputProps,
            className:styles.searchInputField,
            startAdornment: <SearchIcon className={styles.lightIcon} />,
            endAdornment: (loading ? <CircularProgress color="inherit" size={20} /> : null),
          }}
        />
      )}
      size={props?.size ? props.size : 'medium'}
    />
  );
}

SuburbTypeahead.propTypes = {
  size: PropTypes.string,
  scrollToTop: PropTypes.bool,
};

export default SuburbTypeahead;
